import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardHeader, CardTitle } from '../ui/card';
import { ScrollArea } from '../ui/scroll-area';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Bot, Database, Globe, Brain, Activity, XCircle, Pause, Play, Lock, ShieldAlert, BrainCircuit } from 'lucide-react';
import { cn } from '@/lib/utils';
import { MAPLEMYTHOS_TASK_EVENT, type MapleMythosTaskSnapshot } from '@/lib/demythos/taskBus';

type TaskStatus = 'idle' | 'queued' | 'running' | 'paused' | 'completed' | 'failed' | 'stopped';
type TaskType = 'inference' | 'embedding' | 'web-search' | 'analysis' | 'looped-reasoning';

type Task = {
  id: string;
  name: string;
  type: TaskType;
  model?: string;
  status: TaskStatus;
  progress: number;
  startedAt: number;
  updatedAt: number;
  source?: string;
  error?: string;
  isolated?: boolean;
  executionMode?: 'local' | 'webgpu' | 'cloud';
  billable?: boolean;
  loopCount?: number;
  maxLoops?: number;
  creditsUsed?: number;
  tokensUsed?: number;
  restrictions?: {
    simulate: boolean;
    writeAccess: boolean;
    reasoning: boolean;
  };
};

const typeIconMap: Record<TaskType, React.ComponentType<any>> = {
  inference: Bot,
  embedding: Database,
  'web-search': Globe,
  analysis: Brain,
  'looped-reasoning': BrainCircuit,
};

function formatDuration(ms: number) {
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${m}m ${rem}s`;
}

const randomOf = <T,>(arr: T[]) => arr[Math.floor(Math.random() * arr.length)];

const sampleNames = {
  inference: ['Chat response', 'Summarization', 'Plan generation', 'Code completion'],
  embedding: ['Index document chunk', 'Compute embeddings', 'Update vector store'],
  'web-search': ['Query web', 'Fetch URL', 'Scrape content'],
  analysis: ['Analyze CSV', 'Generate briefing', 'Extract insights'],
  'looped-reasoning': ['DeMythos reasoning run'],
};

const sampleModels = ['gpt-4o', 'gpt-4o-mini', 'llama3', 'gemini-1.5-pro', 'deepseek-r1'];

const statusColor: Record<TaskStatus, string> = {
  idle: 'bg-muted text-foreground',
  queued: 'bg-muted text-foreground',
  running: 'bg-emerald-100/60 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  paused: 'bg-amber-100/60 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  completed: 'bg-blue-100/60 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  failed: 'bg-destructive/10 text-destructive',
  stopped: 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
};

const AITaskManager: React.FC = () => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const tickRef = useRef<number | null>(null);
  const [blockedMsg, setBlockedMsg] = useState<string | null>(null);

  useEffect(() => {
    const handler = (ev: Event) => {
      const d = (ev as CustomEvent<any>).detail;
      if (!d?.path) return;
      setBlockedMsg(`Agent access blocked for ${d.path}. Update your allowlist in System Graph.`);
      window.setTimeout(() => setBlockedMsg(null), 3500);
    };
    window.addEventListener('agent:blocked', handler as EventListener);
    return () => window.removeEventListener('agent:blocked', handler as EventListener);
  }, []);

  const runningCount = useMemo(() => tasks.filter((task) => task.status === 'running').length, [tasks]);
  const queuedCount = useMemo(() => tasks.filter((task) => task.status === 'queued').length, [tasks]);

  const addSampleTask = (type?: TaskType) => {
    const t = type ?? randomOf(['inference', 'embedding', 'web-search', 'analysis'] as TaskType[]);
    const name = randomOf(sampleNames[t]);
    const now = Date.now();
    const newTask: Task = {
      id: `${t}-${now}-${Math.random().toString(36).slice(2, 7)}`,
      name,
      type: t,
      model: t === 'inference' || t === 'analysis' ? randomOf(sampleModels) : undefined,
      status: 'running',
      progress: 0,
      startedAt: now,
      updatedAt: now,
      source: 'MapleOS',
    };
    setTasks((prev) => [newTask, ...prev]);
  };

  useEffect(() => {
    const handler = (ev: Event) => {
      const custom = ev as CustomEvent<any>;
      const d = custom.detail;
      if (!d || !d.type || !d.name) return;

      const now = Date.now();
      const task: Task = {
        id: `isolated-${d.type}-${now}-${Math.random().toString(36).slice(2, 7)}`,
        name: d.name,
        type: d.type as TaskType,
        model: d.model,
        status: 'running',
        progress: 0,
        startedAt: now,
        updatedAt: now,
        source: d.source || 'AIIsolation',
        isolated: true,
        restrictions: d.restrictions || { simulate: true, writeAccess: false, reasoning: false },
      };
      setTasks((prev) => [task, ...prev]);
    };
    window.addEventListener('mapleos-start-isolated-task', handler as EventListener);
    return () => window.removeEventListener('mapleos-start-isolated-task', handler as EventListener);
  }, []);

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<MapleMythosTaskSnapshot>).detail;
      if (!detail?.id) return;

      const now = Date.now();
      setTasks((prev) => {
        const existing = prev.find((task) => task.id === detail.id);
        const nextTask: Task = {
          id: detail.id,
          name: detail.name,
          type: 'looped-reasoning',
          model: detail.model,
          status: detail.status,
          progress: detail.progress,
          startedAt: existing?.startedAt || now,
          updatedAt: now,
          source: detail.source,
          executionMode: detail.executionMode,
          billable: detail.billable,
          loopCount: detail.loopCount,
          maxLoops: detail.maxLoops,
          creditsUsed: detail.creditsUsed,
          tokensUsed: detail.tokensUsed,
          error: detail.error,
        };

        if (existing) {
          return prev.map((task) => (task.id === detail.id ? nextTask : task));
        }

        return [nextTask, ...prev];
      });
    };

    window.addEventListener(MAPLEMYTHOS_TASK_EVENT, handler as EventListener);
    return () => window.removeEventListener(MAPLEMYTHOS_TASK_EVENT, handler as EventListener);
  }, []);

  const clearCompleted = () => {
    setTasks((prev) => prev.filter((task) => !['completed', 'failed', 'stopped'].includes(task.status)));
  };

  const stopTask = (id: string) => {
    setTasks((prev) =>
      prev.map((task) =>
        task.id === id ? { ...task, status: 'failed', error: 'Stopped by user', updatedAt: Date.now() } : task
      )
    );
  };

  const togglePause = (id: string) => {
    setTasks((prev) =>
      prev.map((task) =>
        task.id === id
          ? {
              ...task,
              status: task.status === 'running' ? 'paused' : 'running',
              updatedAt: Date.now(),
            }
          : task
      )
    );
  };

  useEffect(() => {
    if (tickRef.current) cancelAnimationFrame(tickRef.current);
    let last = performance.now();

    const loop = (now: number) => {
      const dt = now - last;
      last = now;

      setTasks((prev) =>
        prev.map((task) => {
          if (task.status !== 'running' || task.type === 'looped-reasoning') return task;
          const rate = task.type === 'embedding' ? 0.08 : task.type === 'web-search' ? 0.12 : 0.1;
          const next = Math.min(100, task.progress + rate * dt);
          return {
            ...task,
            progress: next,
            status: next >= 100 ? 'completed' : 'running',
            updatedAt: Date.now(),
          };
        })
      );

      tickRef.current = requestAnimationFrame(loop);
    };

    tickRef.current = requestAnimationFrame(loop);
    return () => {
      if (tickRef.current) cancelAnimationFrame(tickRef.current);
      tickRef.current = null;
    };
  }, []);

  return (
    <div className="flex h-full w-full flex-col">
      {blockedMsg && (
        <div className="flex items-center gap-2 border-b bg-destructive/10 px-4 py-2 text-xs text-destructive sm:text-sm">
          <ShieldAlert className="h-4 w-4" />
          <span className="truncate">{blockedMsg}</span>
        </div>
      )}
      <Card className="rounded-none border-0">
        <CardHeader className="flex flex-col gap-2 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            <CardTitle className="text-lg">AI Task Manager</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="secondary" onClick={() => addSampleTask()}>Start sample task</Button>
            <Button size="sm" variant="ghost" onClick={clearCompleted}>Clear completed</Button>
          </div>
        </CardHeader>
      </Card>

      <div className="flex items-center gap-3 border-b bg-muted/40 px-4 py-2 text-xs sm:text-sm">
        <span className="text-muted-foreground">Running:</span>
        <Badge className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">{runningCount}</Badge>
        <span className="text-muted-foreground">Queued:</span>
        <Badge variant="secondary">{queuedCount}</Badge>
        <span className="ml-auto hidden text-muted-foreground sm:inline">DeMythos runs stream loop progress and credit burn here in real time.</span>
      </div>

      <ScrollArea className="flex-1">
        <div className="space-y-3 p-4">
          {tasks.length === 0 && <div className="text-sm text-muted-foreground">No AI tasks at the moment.</div>}

          {tasks.map((task) => {
            const Icon = typeIconMap[task.type] || Bot;
            const elapsed = formatDuration(task.updatedAt - task.startedAt);

            return (
              <div key={task.id} className="rounded-md border bg-background p-3">
                <div className="flex items-start gap-3">
                  <div className={cn('rounded-md p-2', statusColor[task.status])}>
                    <Icon className="h-4 w-4" />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
                      <div className="truncate font-medium">{task.name}</div>
                      <div className="truncate text-xs text-muted-foreground">• {task.type}</div>
                      {task.model && <div className="truncate text-xs text-muted-foreground">• {task.model}</div>}
                      <div className="flex items-center gap-2 sm:ml-auto">
                        <Badge className={statusColor[task.status]}>{task.status}</Badge>
                        {task.isolated && (
                          <Badge variant="secondary" className="flex items-center gap-1">
                            <Lock className="h-3 w-3" /> Isolated
                          </Badge>
                        )}
                        {task.type === 'looped-reasoning' && task.executionMode && (
                          <Badge variant="outline">{task.executionMode}</Badge>
                        )}
                      </div>
                    </div>

                    <div className="mt-2">
                      <Progress value={task.progress} className="h-2" />
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                        <span>{Math.floor(task.progress)}%</span>
                        <span>•</span>
                        <span>Elapsed {elapsed}</span>
                        {task.loopCount !== undefined && task.maxLoops !== undefined && (
                          <>
                            <span>•</span>
                            <span>Loops {task.loopCount}/{task.maxLoops}</span>
                          </>
                        )}
                        {task.tokensUsed !== undefined && (
                          <>
                            <span>•</span>
                            <span>Tokens {task.tokensUsed}</span>
                          </>
                        )}
                        {task.creditsUsed !== undefined && (
                          <>
                            <span>•</span>
                            <span>Credits {task.creditsUsed > 0 && task.creditsUsed < 0.0001 ? '<0.0001' : task.creditsUsed.toFixed(4)}</span>
                          </>
                        )}

                        {task.billable !== undefined && (
                          <>
                            <span>•</span>
                            <span>{task.billable ? 'Billable' : 'Non-billable'}</span>
                          </>
                        )}
                        {task.isolated && task.restrictions && (
                          <>
                            <span>•</span>
                            <span>{task.restrictions.simulate ? 'Simulated' : 'Real'}</span>
                            <span>{task.restrictions.writeAccess ? 'Write ON' : 'Write OFF'}</span>
                            <span>{task.restrictions.reasoning ? 'Reasoning ON' : 'Reasoning OFF'}</span>
                          </>
                        )}
                        {task.error && (
                          <>
                            <span>•</span>
                            <span className="text-destructive">{task.error}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {task.status === 'running' && task.type !== 'looped-reasoning' && (
                      <>
                        <Button size="icon" variant="ghost" title="Pause task" aria-label="Pause task" onClick={() => togglePause(task.id)}>
                          <Pause className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" title="Stop task" aria-label="Stop task" onClick={() => stopTask(task.id)}>
                          <XCircle className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                    {task.status === 'paused' && task.type !== 'looped-reasoning' && (
                      <>
                        <Button size="icon" variant="ghost" title="Resume task" aria-label="Resume task" onClick={() => togglePause(task.id)}>
                          <Play className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" title="Stop task" aria-label="Stop task" onClick={() => stopTask(task.id)}>
                          <XCircle className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
};

export default AITaskManager;